import React, { ReactNode, useContext, useEffect, useState } from 'react';

import UbiquitiHostWifiBroadcastsContext from '@totem/components/ubiquiti/hosts/details/wifiBroadcasts/ubiquitiHostWifiBroadcastsContext';
import {
  UbiquitiApiDataItem,
  UbiquitiWifiBroadcast,
} from '@totem/components/ubiquiti/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { UBIQUITI_HOSTS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  hostId: string;
  children?: ReactNode;
};

// Standalone by design (takes hostId as a prop, not from route params) so it
// can be embedded anywhere a host's WiFi broadcast list is needed, not just
// the details drill-down page.
const UbiquitiHostWifiBroadcastsContainer = ({ hostId, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const [wifiBroadcasts, setWifiBroadcasts] = useState<
    UbiquitiApiDataItem<UbiquitiWifiBroadcast>[]
  >([]);

  useEffect(() => {
    setLoading(true);

    setError(false);

    // Host IDs contain a literal colon (e.g. "D8B3...:1815601434"), which is
    // legal in a URL path segment per RFC 3986 but not guaranteed to survive
    // every intermediate proxy/gateway unencoded — encode defensively.
    fetch(
      `${UBIQUITI_HOSTS_ENDPOINT}/${encodeURIComponent(hostId)}/wifiBroadcasts`,
      {
        method: 'GET',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
      },
    )
      .then((res) => {
        // A non-2xx response body is an APIErrorResponse object, not a
        // broadcast array — never hand that to the table as dataSource.
        if (!res.ok) {
          setError(true);
          return [];
        }
        return res.json();
      })
      .then((result: UbiquitiApiDataItem<UbiquitiWifiBroadcast>[]) => {
        setWifiBroadcasts(Array.isArray(result) ? result : []);
      })
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setError(true);

        setWifiBroadcasts([]);

        setLoading(false);
      });
  }, [hostId, getApiToken]);

  return (
    <UbiquitiHostWifiBroadcastsContext.Provider
      value={{ loading, error, wifiBroadcasts }}
    >
      <div>{children}</div>
    </UbiquitiHostWifiBroadcastsContext.Provider>
  );
};

export default UbiquitiHostWifiBroadcastsContainer;
